import { MoreOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Form, Switch, Tooltip } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowStep } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';

import { StepDetailsFormFields } from './StepDetailsFormFields';

type Props = {
  /** The step being edited; null hides the modal. */
  step: WorkflowStep | null;
  onCancel: () => void;
  onSubmit: (values: {
    titleEn: string;
    titleFr?: string;
    /** Rail / Application Review label. Always sent by this modal — including as `''`, which is
     *  how the originator clears it back to "use the Title". The API distinguishes an empty
     *  string (clear) from an omitted field (leave as is); see `EditWorkflowStepRequest`. */
    navTitleEn: string;
    navTitleFr: string;
    descriptionEn?: string;
    descriptionFr?: string;
    /** Only sent for Dynamic Form steps — the only type the BE lets carry the flag.
     *  Undefined for every other type, which the BE reads as "leave as is". */
    advisorOnly?: boolean;
  }) => Promise<void>;
  /** Opens the save-as-custom naming modal for this step. Omitted (no third button) for non-DynamicForm steps. */
  onSaveAsCustom?: () => void;
  /** Opens the Advanced panel (form branching). Omitted = no `…` in the header, which is
   *  how non-DynamicForm steps get no trigger at all. */
  onAdvanced?: () => void;
};

/** Title + description editor. Step type is immutable — to change type, delete + re-add. */
export function EditStepModal({ step, onCancel, onSubmit, onSaveAsCustom, onAdvanced }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const open = step !== null;
  // Only Dynamic Form steps may be withheld from the public runtime — the BE refuses the
  // flag on every other type, because they are either the public flow's structure (a
  // customer with no PDF Generation step has no ending) or already withheld by type.
  const supportsAdvisorOnly = step?.type === 'DynamicForm';

  useEffect(() => {
    if (step) {
      form.setFieldsValue({
        titleEn: step.titleEn,
        titleFr: step.titleFr,
        navTitleEn: step.navTitleEn ?? '',
        navTitleFr: step.navTitleFr ?? '',
        descriptionEn: step.descriptionEn,
        descriptionFr: step.descriptionFr,
        advisorOnly: step.advisorOnly === true,
      });
    } else {
      form.resetFields();
    }
  }, [step, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({
      titleEn: values.titleEn,
      titleFr: values.titleFr ?? '',
      // Always a string, never undefined: this modal OWNS the nav title, so an emptied field has
      // to travel as "" (clear it) rather than vanishing from the payload (leave it alone).
      navTitleEn: values.navTitleEn ?? '',
      navTitleFr: values.navTitleFr ?? '',
      descriptionEn: values.descriptionEn ?? '',
      descriptionFr: values.descriptionFr ?? '',
      // Undefined rather than false for a type that cannot carry the flag: the BE reads
      // that as "not mentioned" and leaves the stored value alone, so renaming a step can
      // never clear a setting this modal did not show.
      advisorOnly: supportsAdvisorOnly ? values.advisorOnly === true : undefined,
    });
  };

  return (
    <BmoModal
      open={open}
      onCancel={onCancel}
      title={t('workflow.steps.editModalTitle')}
      width={560}
      headerExtra={
        onAdvanced ? (
          <Tooltip title={t('workflow.steps.advanced.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined style={{ color: '#fff' }} />}
              aria-label={t('workflow.steps.advanced.tooltip')}
              onClick={onAdvanced}
            />
          </Tooltip>
        ) : undefined
      }
      footer={[
        ...(onSaveAsCustom
          ? [
              <Button key="saveAsCustom" icon={<SaveOutlined />} onClick={onSaveAsCustom}>
                {t('workflow.steps.saveAsCustomStep')}
              </Button>,
            ]
          : []),
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleOk}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <StepDetailsFormFields />
        {supportsAdvisorOnly && (
          <Form.Item
            name="advisorOnly"
            valuePropName="checked"
            label={t('workflow.steps.fields.advisorOnly')}
            extra={t('workflow.steps.fields.advisorOnlyHelp')}
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        )}
      </Form>
    </BmoModal>
  );
}
